import { useLazyQuery } from '@apollo/client';
import {
  SailthruFallbackNewestDocument,
  SailthruFallbackPodcastDocument,
  SailthruFallbackTrendingDocument
} from 'api';
import { initializeApollo } from 'lib/graphql/client';
import { SailthruSections } from './types';

const apolloClient = initializeApollo();

type FallbackConfig = {
  document:
    | typeof SailthruFallbackNewestDocument
    | typeof SailthruFallbackTrendingDocument
    | typeof SailthruFallbackPodcastDocument;
  variables?: Record<string, string | number>;
  dataPath: string[];
};

/**
 * Configuration object for fallback content queries by section
 *
 * Each section config contains:
 * - document: The GraphQL query document to execute
 * - variables: Optional variables to pass to the query
 * - dataPath: Array of keys to traverse to get the data from the response
 *
 * Sections:
 * - RecommendationsNewest: Latest talks
 * - RecommendationsTrending: Popular TEDx talks (6)
 * - RecommendationsAI: Trending AI talks (3)
 * - RecommendationsPsychology: Trending psychology talks (3)
 * - RecommendationsTEDEd: Trending animated talks (3)
 * - RecommendationsCountdown: Trending countdown talks (3)
 * - RecommendationsPodcast: Latest podcast episodes
 */
const FALLBACK_CONFIGS: Record<string, FallbackConfig> = {
  [SailthruSections.RecommendationsNewest]: {
    document: SailthruFallbackNewestDocument,
    dataPath: ['newestTalks', 'nodes']
  },
  [SailthruSections.RecommendationsTrending]: {
    document: SailthruFallbackTrendingDocument,
    variables: {
      topicSlug: 'tedx',
      first: 6
    },
    dataPath: ['trendingTalks', 'videos', 'nodes']
  },
  [SailthruSections.RecommendationsAI]: {
    document: SailthruFallbackTrendingDocument,
    variables: {
      topicSlug: 'ai',
      first: 3
    },
    dataPath: ['trendingTalks', 'videos', 'nodes']
  },
  [SailthruSections.RecommendationsPsychology]: {
    document: SailthruFallbackTrendingDocument,
    variables: {
      topicSlug: 'psychology',
      first: 3
    },
    dataPath: ['trendingTalks', 'videos', 'nodes']
  },
  [SailthruSections.RecommendationsTEDEd]: {
    document: SailthruFallbackTrendingDocument,
    variables: {
      topicSlug: 'animation',
      first: 3
    },
    dataPath: ['trendingTalks', 'videos', 'nodes']
  },
  [SailthruSections.RecommendationsCountdown]: {
    document: SailthruFallbackTrendingDocument,
    variables: {
      topicSlug: 'countdown',
      first: 3
    },
    dataPath: ['trendingTalks', 'videos', 'nodes']
  },
  [SailthruSections.RecommendationsPodcast]: {
    document: SailthruFallbackPodcastDocument,
    dataPath: ['podcasts', 'videos', 'nodes']
  }
};

/**
 * Custom hook to fetch fallback content data for a given section
 *
 * @param section - The section identifier to fetch fallback data for (e.g. 'RecommendationsAI', 'RecommendationsPsychology')
 * @param variables - Optional variables to override or merge with default section variables
 * @returns {Object} An object containing:
 *   - data: The formatted fallback data for the section
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetch
 *   - fetchFallbackContent: Function to manually trigger fetching the fallback content
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchFallbackContent } = useFallbackData('RecommendationsAI');
 *
 * useEffect(() => {
 *   if (isEmpty(data)) {
 *     fetchFallbackContent();
 *   }
 * }, [data, fetchFallbackContent]);
 * ```
 *
 * @example With custom variables
 * ```tsx
 * const { data, loading, error, fetchFallbackContent } = useFallbackData(
 *   'RecommendationsNewest',
 *   { first: 5 }
 * );
 * ```
 */
export const useFallbackData = (
  section: string,
  customVariables?: Record<string, string | number>
) => {
  const config = FALLBACK_CONFIGS[section];
  if (!config) {
    console.warn(`No fallback config found for section: ${section}`);
    return { data: [], loading: false, error: null };
  }

  // Merge custom variables with default config variables
  const mergedVariables = {
    ...config.variables,
    ...customVariables
  };

  const [loadQuery, { error, data, loading }] = useLazyQuery(config.document, {
    variables: mergedVariables,
    client: apolloClient
  });

  const fallbackData =
    config.dataPath.reduce((acc, key) => acc?.[key], data) || [];

  return {
    data: fallbackData,
    loading,
    error,
    fetchFallbackContent: async () => {
      const result = await loadQuery({
        variables: mergedVariables
      });
      return {
        data:
          config.dataPath.reduce((acc, key) => acc?.[key], result.data) || [],
        loading: false,
        error: result.error
      };
    }
  };
};
